import React, { useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Paper } from '@mui/material';
import {
  AddKnowledgeDocumentRequest,
  KnowledgeDocument,
  KnowledgeService,
} from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import ConfirmDialog from '../common/ConfirmDialog';
import PageStyles from '../../styles/PageStyles';
import DateUtil from '../../util/DateUtil';
import DisplayService from '../../util/DisplayService';

const CONTENT_PREVIEW_LENGTH = 100;

function KnowledgeDocumentsLookup(props: RouteComponentProps) {
  const { history } = props;
  const tableRef = React.useRef<TableInstance>();
  const { classes } = PageStyles();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<
    KnowledgeDocument | undefined
  >();

  const refreshData = () => tableRef.current?.fetchData();

  const showError = (error: unknown, fallback: string) => {
    const displayed = DisplayService.getErrorResponse(error, fallback);

    toast.error(displayed.message);
  };

  const addItem = () => {
    history.push('/knowledge-documents/add');
  };

  const editItem = async (item: any) => {
    const original: KnowledgeDocument = item.row.original;

    if (!original.id) return;

    const data: AddKnowledgeDocumentRequest = {
      title: item.values.title ?? original.title,
      content: item.values.content ?? original.content,
      sourceType: item.values.sourceType ?? original.sourceType,
      sourceUrl: item.values.sourceUrl ?? original.sourceUrl ?? null,
    };

    try {
      await KnowledgeService.update(original.id, data);
      toast.success('Knowledge document updated successfully');
      refreshData();
    } catch (error) {
      showError(error, 'Error updating knowledge document');
      refreshData();
    }
  };

  const requestDelete = (rowData: KnowledgeDocument) => {
    if (!rowData.id) return;

    setDocumentToDelete(rowData);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    const target = documentToDelete;

    setOpenDeleteDialog(false);

    if (!target?.id) return;

    try {
      await KnowledgeService.delete(target.id);
      toast.success('Knowledge document deleted');
      refreshData();
    } catch (error) {
      showError(error, 'Error deleting knowledge document');
    } finally {
      setDocumentToDelete(undefined);
    }
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'knowledgeDocuments',
        header: 'Knowledge Documents',
        columns: [
          {
            accessorKey: 'id',
            header: 'ID',
            enableEditing: false,
            size: 60,
          },
          {
            accessorKey: 'title',
            header: 'Title',
            size: 240,
          },
          {
            accessorKey: 'sourceType',
            header: 'Source Type',
            size: 140,
          },
          {
            accessorKey: 'sourceUrl',
            header: 'Source URL',
            size: 220,
            Cell: ({ cell }) => {
              const url = cell?.row?.original?.sourceUrl;

              if (!url) return null;

              return (
                <a href={url} target='_blank' rel='noreferrer'>
                  {url}
                </a>
              );
            },
          },
          {
            accessorKey: 'content',
            header: 'Content',
            muiTableBodyCellEditTextFieldProps: {
              multiline: true,
            },
            Cell: ({ cell }) => {
              const content = cell?.row?.original?.content ?? '';
              const preview =
                content.length > CONTENT_PREVIEW_LENGTH
                  ? `${content.slice(0, CONTENT_PREVIEW_LENGTH).trimEnd()}…`
                  : content;

              return (
                <div
                  style={{
                    minWidth: 320,
                    maxWidth: 480,
                    whiteSpace: 'pre-wrap',
                    paddingLeft: 8,
                  }}
                  title={content}
                >
                  {preview}
                </div>
              );
            },
          },
          {
            header: 'Created',
            accessorFn: row =>
              row?.createdAt ? DateUtil.getReadableDateTime(row.createdAt) : '',
            enableEditing: false,
            size: 160,
          },
        ],
      },
    ],
    [],
  );

  return (
    <Paper className={classes.paper}>
      <ConfirmDialog
        confirmAction={confirmDelete}
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        title='Confirm Knowledge Document Deletion'
        text={`Are you sure you want to delete "${
          documentToDelete?.title ?? 'this document'
        }"? This cannot be undone.`}
      />
      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={KnowledgeService.getAll}
        addAction={addItem}
        editAction={editItem}
        deleteAction={requestDelete}
        enableInlineEditing
      />
    </Paper>
  );
}

export default withRouter(KnowledgeDocumentsLookup);
